import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import UpdateIcon from '@material-ui/icons/Update';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import PinIcon from '../icons/PinIcon';
import NotAvailable from '../atoms/NotAvailable';
import Avatar from '../atoms/Avatar';
import Download from '../atoms/Download';
import DropDown from '../atoms/DropDown';
import DragNDropDots from '../atoms/DragNDrop';
import LocationIcon from '../atoms/LocationIcon';
import Score, { SCORE_TYPE } from '../atoms/Score';
import Loader from '../atoms/Loader';
import Toast, { ToastType } from '../atoms/Toast';
import Action from '../atoms/Action';
import ConfirmDialog from './ConfirmDialog';
import PlayerNameWithPopover from './PlayerNameWithPopover';
import gql, { serializeGQLObject } from '../services/gql';
import exportXLSReport, { ERROR_MESSAGE_CHECK_EMAIL, XLSExportType } from '../services/export-xls-report';
import exportPDFReport, { PDFExportPage } from '../services/export-pdf-report';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import {
  COLOR_BACKGROUND_LIGHT,
  COLOR_TEXT,
  COLOR_DARK_GRAY,
  COLOR_WHITE,
  COLOR_BORDER,
  COLOR_MIDDLE_GRAY,
  COLOR_BLUE,
  COLOR_ORANGE,
  COLOR_BLACK,
} from '../styles/colors';
import MEDIA from '../styles/media';
import User from '../types/User';
import PinnedPlayer from '../types/PinnedPlayer';
import Position from '../types/Position';
import HighSchool from '../types/HighSchool';
import College from '../types/College';
import Checkbox from '../atoms/Checkbox';
import { SortableTableHeaderCell } from '../atoms/SortableTableHeaderCell';
import CompareIcon from '../icons/CompareIcon';
import AlertIcon from '../icons/AlertIcon';
import SaveIcon from '../icons/SaveIcon';
import Player from '../types/Player';
import SideBySideIcon from '../icons/SideBySideIcon';
import { getGradClassYearsList } from '../services/user';
import Typography from '@material-ui/core/Typography';
import MultiSelect from '../molecules/MultiSelect';
import Button from '../atoms/Button';
import PlusIcon from '../icons/PlusIcon';
import Dialog from '../molecules/Dialog';
import Input from '../atoms/Input';
import Board from '../types/Board';
import RefreshIcon from '@material-ui/icons/RefreshRounded';

interface MyEvaluationBoardProps {
  className?: string;
  user: User;
  position?: Position;
  printed?: boolean;
  rolloverDayNfl?: number;
  rolloverMonthNfl?: number;
  currentRecruitingYear?: number;
}

export const TEAM_TYPE = {
  COLLEGE: 'College',
  NFL: 'NFL',
};

export const FILTER_TYPE = {
  POSITION: 'position',
  CLASS: 'class',
  COLLEGE_TYPE: 'collegeType'
}

const useStyles = makeStyles<Theme, MyEvaluationBoardProps>(theme => ({
  myEvaluationBoard: {
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    position: 'relative',
  },

  header: {
    display: 'flex',
    minHeight: '70px',
    padding: theme.spacing(2),
    alignItems: 'center',
  },
  headerTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing(1.5),
  },
  headerIcon: {
    flexShrink: 0,
    width: '22px',
    height: '22px',
    color: COLOR_TEXT,
  },
  headerTitle: {
    ...theme.typography.h2,
    margin: theme.spacing(0,0,0,1),
  },
  headerDownloads: {
    display: 'flex',
    marginLeft: 'auto',
  },
  closeButton: {
    marginLeft: 'auto',
  },
  dropDown: {
    display: 'inline-block',
    maxWidth: 120,
    minWidth: 120,
  },
  dropDownSelect: {
    width: '100%',
    height: '40px',
  },

  tableWrap: {
    maxHeight: '3200px',
    overflow: 'auto',
  },

  noPlayers: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontFamily: FONT_PROXIMA_NOVA,
  },
  noPlayersIcon: {
    width: '40px',
    height: '40px',
    opacity: 0.6,
  },
  noPlayersText: {
    marginTop: theme.spacing(2),
    fontSize: '14px',
    lineHeight: '1em',
    color: COLOR_DARK_GRAY,
  },

  tableHeader: {
    position: 'relative',
  },
  tableHeadCell: {
    padding: theme.spacing(1, 0, 1, 1),
    fontWeight: 'normal',
    fontSize: theme.typography.pxToRem(14),
    lineHeight: '1rem',
    color: COLOR_DARK_GRAY,
    borderBottom: `1px solid ${COLOR_BORDER}`,

    '&:first-of-type': {
      paddingLeft: theme.spacing(1.5),
    },
    '&:nth-of-type(2)': {
      paddingLeft: theme.spacing(1.5),
    },
    '&:last-of-type': {
      paddingRight: theme.spacing(1.5),
    },
  },
  tableRow: {
    backgroundColor: COLOR_WHITE,
    transition: 'background 0.3s',

    '&:hover': {
      backgroundColor: COLOR_BACKGROUND_LIGHT,

      '& $dragNDropDots': {
        opacity: 1,
      },
    },
  },
  tableRowDragging: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tableCell: {
    padding: theme.spacing(1, 0, 1, 1.5),
    border: 0,

    '&:first-of-type': {
      paddingLeft: (props) => props.printed ? theme.spacing(1) : 0,
    },
    '&:nth-of-type(2)': {
      paddingLeft: (props) => props.printed ? theme.spacing(1) : 0,
    },
    '&:last-of-type': {
      paddingLeft: (props) => props.printed ? theme.spacing(1) : theme.spacing(1),
    },
  },

  sportIcon: {
    flexShrink: 0,
  },

  tableCellWithDots: {
    width: 20,
    minWidth: 20,
  },

  tableSectionTitle: {
    background: COLOR_BACKGROUND_LIGHT,
    color: COLOR_DARK_GRAY,
    fontSize: '12px',
    lineHeight: '12px',
    padding: theme.spacing(0.5, 2),
    borderTop: '1px solid rgba(0, 0, 0, 0.04)',
    borderBottom: 0,
    position: 'relative',

    '&:after': {
      content: '""',
      position: 'absolute',
      width: '100%',
      height: '1px',
      background: 'rgba(0, 0, 0, 0.04)',
      bottom: 0,
      left: 0,
    },
  },

  playerInfoContainer: {
    display: 'flex',
    alignItems: 'center',
  },

  playerAvatar: {
    flexShrink: 0,
    width: '32px',
    height: '32px',
  },
  playerNameAndCollege: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: theme.spacing(1),
  },

  playerCollege: {
    fontSize: '12px',
    color: COLOR_DARK_GRAY,
  },

  locationIcon: {
    marginLeft: 'auto',
  },
  dragNDropDots: {
    opacity: 0,
    transition: 'opacity 0.2s',
  },
  blankCell: {
    display: 'none',
  },

  '@media (max-width: 1600px)': {
    dropDownSelect: {
      fontSize: theme.typography.pxToRem(14),
    },
  },

  [MEDIA.DESKTOP_LARGE]: {
    tableHeadCell: {
      padding: theme.spacing(1, 0, 1, 1.5),
      fontSize: theme.typography.pxToRem(14),

      '&:first-of-type': {
        paddingLeft: theme.spacing(1.5),
      },
      '&:last-of-type': {
        paddingRight: theme.spacing(1.5),
      },
    },
  },
  '@media (min-width: 1025px) and (max-width: 1260px), (max-width: 480px)': {
    header: {
      flexWrap: 'wrap',
    },
    headerTitleWrap: {
      width: '100%',
      marginBottom: theme.spacing(1),
    },
    headerDownloads: {
      marginLeft: 0,
    },
  },

  [MEDIA.PRINT]: {
    header: {
      display: 'none',
    },
  },
  multiSportIcon: {
    color: COLOR_WHITE
  },
  checkBoxCell: {
    textAlign: 'center',
  },
  orderIcon: {
    position: 'relative',
    right: '8px',
    width: '32px',
    height: '32px',
    color: COLOR_MIDDLE_GRAY,
  },
  orderIconActive: {
    color: COLOR_BLUE,
  },
  actions: {
    display: 'flex',
    padding: theme.spacing(2.5),
    borderTop: `1px solid ${COLOR_BORDER}`,
  },
  actionsPrimary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    maxWidth: '560px',
    paddingRight: theme.spacing(4.25),
  },
  action: {
    padding: theme.spacing(0,0,0,2),
    color: COLOR_DARK_GRAY,
    outlineColor: COLOR_ORANGE,
    transition: 'color 0.3s',

    '&:first-child': {
      paddingLeft: 0
    },

    '&:hover': {
      color: COLOR_BLUE,
      textDecoration: 'underline',

      '& $actionText': {
        color: COLOR_BLUE,
      },
    },
  },
  disabledAction: {
    outlineColor: 'transparent',

    '& $actionText': {
      color: COLOR_DARK_GRAY,
    },

    '&:hover': {
      color: COLOR_DARK_GRAY,
      textDecoration: 'none',

      '& $actionText': {
        color: COLOR_DARK_GRAY,
      },
    },
  },
  actionIcon: {
    width: '20px',
    height: '20px',
  },
  actionText: {
    // display: 'none',
    fontSize: theme.typography.pxToRem(16),
    color: COLOR_TEXT,
    transition: 'color 0.3s',
  },
  seasonalBlock: {
    '& .MuiTypography-root': {
      fontSize: theme.typography.pxToRem(14),
    }
  },
  careerLogHeadCell: {
    padding: theme.spacing(1),
  },
  hsComTableCell: {
    padding: theme.spacing(1, 1, 1, 2.5),
  },
  educationDropDown: {
    flexShrink: 0,
    // marginBottom: theme.spacing(1),
  },
  educationDropDownSelectorRoot: {
    // boxSizing: 'border-box',
    paddingRight: theme.spacing(5),
    // minHeight: '40px',
  },
  [MEDIA.MOBILE]: {
    educationDropDown: {
      margin: theme.spacing(1,3),
    },
  },
  noDataTextWrapper: {
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    height: "100%",
    minHeight: 200,
    width: "100%"
  },
  noDataText: {
    color: COLOR_DARK_GRAY,
    fontSize: theme.typography.pxToRem(14),
    margin: '0 auto',
  },
  multiSelect: {
    maxWidth: '130px',
    marginLeft: '6px',
    width: '100%',
  },
  multiSelectSelector: {
    height: '42px',
  },
  multiSelectSelectorRoot: {
    height: '28px',
    lineHeight: '28px',
    fontSize: theme.typography.pxToRem(16),
  },
  filterWrap: {
    display: 'flex',
    padding: theme.spacing(1.25, 2.5),

    '& > $multiSelect:first-of-type': {
      maxWidth: 130,
    },

    '& $multiSelect': {
      marginLeft: theme.spacing(0), 
      paddingRight: theme.spacing(2),
    },
  },
  addBoardButton: {
    display: 'flex',
    float: 'right',
    width: 'auto',
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.75),

    '& span': {
      fontSize: theme.typography.pxToRem(14),
    }
  },
  addIcon: {
    flexShrink: 0,
    width: '12px',
    height: '12px',
    marginRight: theme.spacing(1),
  },
  boardDialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  boardDialogInput: {
    width: '100%',
  },
  boardFilterWrap: {
    display: 'flex',
    marginLeft: 'auto',
    alignItems: 'center',
  },
  tfPosPercCell: {
    paddingRight: theme.spacing(1),
  },
  selectorRootClassName: {
    marginRight: theme.spacing(3.75),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  colorPrimary: {
    color: COLOR_BLUE,
  }
}), { name: MyEvaluationBoard.name });

export default function MyEvaluationBoard (props:MyEvaluationBoardProps) {
  const {
    className,
    user,
    position,
    printed = false,
    rolloverDayNfl,
    rolloverMonthNfl,
    currentRecruitingYear,
  } = props;
  const classes = useStyles(props);
  const programId = user?.program?.id;
  const userId = user?.id;
  const isCollege = user?.accessLevel?.type === 'college';

  const [players, setPlayers] = useState<PinnedPlayer[]>([]);
  const [pinnedPlayers, setPinnedPlayers] = useState<PinnedPlayer[]>([]);
  const [positions, setPositions] = useState<any>([]);
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [graduatingClasses, setGraduatingClasses] = useState<any>([]);
  const [selectedGradClasses, setSelectedGradClasses] = useState<string[]>([]);
  const [playersIdsMapping, setPlayersIdsMapping] = useState<any>();

  const [loading, setLoading] = useState<boolean>(true);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [toastVisible, setToastVisible] = useState<boolean>(false);
  const [toastType, setToastType] = useState<ToastType>(ToastType.SUCCESS);
  const [toastMessage, setToastMessage] = useState<any>('');
  const [selectedPlayers, setSelectedPlayers] = useState<Player[]>([]);
  const [sortConfig, setSortConfig] = React.useState<{ 
    metric: 'pai' | 'combine' | 'class' | 'ppi' | 'posRank' | 'name' | null;
    order: 'asc' | 'desc' | undefined;
  }>({ metric: null, order: undefined });
  const [selectedConference, setSelectedConference] = useState<any[]>([
    { content: 'P4', value: 'P4' },
    { content: 'G6', value: 'G6' },
    { content: 'FBS Indy', value: 'FBS Indy' },
    { content: 'FCS', value: 'FCS' },
    { content: 'Div II', value: 'Div II' },
    { content: 'Div III', value: 'Div III' },
    { content: 'NFL', value: 'NFL' },
  ]);
  const [selectedCollegeType, setSelectedCollegeType] = useState<string[]>([]);
  const [newBoardName, setNewBoardName] = useState<string>('');
  const [boardDialogOpen, setBoardDialogOpen] = useState<boolean>(false);
  const [deleteBoardDialogOpen, setDeleteBoardDialogOpen] = useState<boolean>(false);
  const [boardsList, setBoardsList] = useState<Board[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<string>('');
  const [selectedBoardDetails, setSelectedBoardDetails] = useState<Board>();
  const [refreshBoards, setRefreshBoards] = useState<boolean>(false);

  // constants
  const isDragDisabled = (
    (!selectedPositions?.length &&
    !selectedGradClasses?.length &&
    !selectedCollegeType?.length) ||

    selectedPositions?.includes('all') ||
    selectedGradClasses?.includes('all') ||
    selectedCollegeType?.includes('all')
  );
  const disableNewBoard = !selectedPositions?.length && !selectedGradClasses?.length && !selectedCollegeType?.length;
  const boardDropdownItems = boardsList?.length
    ? boardsList.map(board => ({
        content: board.name,
        value: board.id.toString()
      })).sort((a, b) => a.content.localeCompare(b.content))
    : [];

  useEffect(() => {
    fetchPinnedPlayers();
    getBoards();
  }, []);

  // refresh
  useEffect(() => {
    if (refreshBoards) {
      getBoards();
      fetchPinnedPlayers();
      setRefreshBoards(false);
    }
  }, [refreshBoards]);

  useEffect(() => {
    if (boardsList.length) {
      // let order = boardsList.find(board => board.id === Number(selectedBoard))?.order || [];
      if (selectedBoardDetails) {
        let order = selectedBoardDetails?.order || [];
        updateAndSetPlayersData(orderPlayers(pinnedPlayers, order));
      }
    }
  }, [boardsList, selectedBoardDetails]);

  useEffect(() => {
    // Skip if no players
    if (!players.length) return;
  
    // If no filters, show all once (optional)
    if (!selectedGradClasses?.length && !selectedPositions?.length && !selectedCollegeType?.length) {
      setPinnedPlayers([...players].sort((a, b) => a.order - b.order));
      return;
    }
  
    // Filter by grad class and/or position
    let filtered = [...players];
  
    if (!!selectedGradClasses?.length) {
      const gradClassesAsStrings: string[] = selectedGradClasses.map(String);
      filtered = filtered.filter(
        (p: PinnedPlayer) =>
        gradClassesAsStrings.includes(String(p.player?.highSchools?.[0]?.graduatingClass))
      );
    }
  
    if (!!selectedPositions?.length) {
      filtered = filtered.filter(
        (p: PinnedPlayer) => selectedPositions.includes(p.position)
      );
    }
    
    if (selectedCollegeType.length) {
      const playersBeforeFilter = filtered;
      // const playersAfterFilterFCS = playersBeforeFilter.filter((p: PinnedPlayer) => {
      //   const primaryCollege = (p.player?.playerColleges || []).find((college:College) => !!college?.isPrimary);
      //   if (primaryCollege) {
      //     return primaryCollege.team?.conference?.id && selectedCollegeType.includes(primaryCollege.team?.conference?.id.toString());
      //   }
      //   return false;
      // });
      let playersAfterFilterP4: PinnedPlayer[] = [];
      if (selectedCollegeType.includes('P4')) {
        playersAfterFilterP4 = playersBeforeFilter.filter((p: PinnedPlayer) => {
          const primaryCollege = (p.player?.playerColleges || []).find((college:College) => !!college?.isPrimary);
          if (primaryCollege) {
            return primaryCollege.team?.conference?.level?.includes('P4 ');
          }
          return false;
        });
      }
      let playersAfterFilterG6: PinnedPlayer[] = [];
      if (selectedCollegeType.includes('G6')) {
        playersAfterFilterG6 = playersBeforeFilter.filter((p: PinnedPlayer) => {
          const primaryCollege = (p.player?.playerColleges || []).find((college:College) => !!college?.isPrimary);
          if (primaryCollege) {
            return primaryCollege.team?.conference?.level?.includes('G6 ');
          }
          return false;
        });
      }
      let playersAfterFilterFBS: PinnedPlayer[] = [];
      if (selectedCollegeType.includes('FBS')) {
        playersAfterFilterFBS = playersBeforeFilter.filter((p: PinnedPlayer) => {
          const primaryCollege = (p.player?.playerColleges || []).find((college:College) => !!college?.isPrimary);
          if (primaryCollege) {
            return primaryCollege.team?.conference?.name == 'FBS Independents';
          }
          return false;
        });
      }
      let playersAfterFilterFCS: PinnedPlayer[] = [];
      if (selectedCollegeType.includes('FCS')) {
        playersAfterFilterFCS = playersBeforeFilter.filter((p: PinnedPlayer) => {
          const primaryCollege = (p.player?.playerColleges || []).find((college:College) => !!college?.isPrimary);
          if (primaryCollege) {
            return primaryCollege.team?.conference?.level?.includes('FCS ');
          }
          return false;
        });
      }
      let playersAfterFilterDivII: PinnedPlayer[] = [];
      if (selectedCollegeType.includes('Div II')) {
        playersAfterFilterDivII = playersBeforeFilter.filter((p: PinnedPlayer) => {
          const primaryCollege = (p.player?.playerColleges || []).find((college:College) => !!college?.isPrimary);
          if (primaryCollege) {
            return primaryCollege.team?.conference?.level?.includes('Div II ');
          }
          return false;
        });
      }
      let playersAfterFilterDivIII: PinnedPlayer[] = [];
      if (selectedCollegeType.includes('Div III')) {
        playersAfterFilterDivIII = playersBeforeFilter.filter((p: PinnedPlayer) => {
          const primaryCollege = (p.player?.playerColleges || []).find((college:College) => !!college?.isPrimary);
          if (primaryCollege) {
            return primaryCollege.team?.conference?.level?.includes('Div III ');
          }
          return false;
        });
      }
      let playersAfterFilterNFL: PinnedPlayer[] = [];
      if (selectedCollegeType.includes('NFL')) {
        playersAfterFilterNFL = playersBeforeFilter.filter(
          (p) => p.player?.hasHighSchool === true && p.player?.hasCollegeTeam === true && p.player?.hasNFLTeam === true
        );
      }

      filtered = [...playersAfterFilterP4, ...playersAfterFilterG6, ...playersAfterFilterFCS, ...playersAfterFilterFBS, ...playersAfterFilterDivII, ...playersAfterFilterDivIII, ...playersAfterFilterNFL];
    }
  
    // sorting
    if (sortConfig?.metric && sortConfig?.order) {
      const descending = sortConfig.order === 'desc';
      filtered = sortPlayersByPrimaryMetric(filtered, sortConfig.metric, descending);
    } else {
      if (selectedBoardDetails) {
        // const order = boardsList.find(board => board.id === Number(selectedBoard))?.order || [];
        const order = selectedBoardDetails?.order || [];
        filtered = orderPlayers(filtered.sort((a, b) => a.order - b.order), order);
      } else {
        filtered = orderPlayersByOrder(filtered);
      }
    }
    setPinnedPlayers(filtered);
  }, [selectedGradClasses, selectedPositions, players, sortConfig, selectedCollegeType]);  

  React.useEffect(() => {

    if (!!sortConfig?.order) {
      if (!sortConfig.metric || !sortConfig.order) {
        // setPlayers(players); // no sort
        setPinnedPlayers(pinnedPlayers); // no sort
        return;
      }
      
      const descending = sortConfig.order === 'desc';
    
      const sorted = sortPlayersByPrimaryMetric(pinnedPlayers, sortConfig.metric, descending);
      // setPlayers(sorted);
      setPinnedPlayers(sorted);
    }
  }, [sortConfig]);  

  function sortPlayersByPrimaryMetric(
    players: any[],
    metricKey: 'pai' | 'combine' | 'class' | 'ppi' | 'posRank' | 'name',
    descending: boolean
  ) {
    return [...players].sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;
      const IS_CLASS_METRIC = metricKey === 'class';
      const IS_POS_RANK_METRIC = metricKey === 'posRank';
      const IS_NAME_METRIC = metricKey === 'name';
  
      if (IS_CLASS_METRIC) {
        aVal = a.player?.highSchools?.find((hs: any) => hs?.isPrimary)?.graduatingClass ?? 0;
        bVal = b.player?.highSchools?.find((hs: any) => hs?.isPrimary)?.graduatingClass ?? 0;
      } else if (metricKey === 'ppi') {
        aVal = a.player?.ppi ?? 0;
        bVal = b.player?.ppi ?? 0;
      } else if (IS_POS_RANK_METRIC) {
        aVal = a.player?.tfPosPerc ?? 0;
        bVal = b.player?.tfPosPerc ?? 0;
      } else if (IS_NAME_METRIC) {
        aVal = a.player?.firstName ?? '';
        bVal = b.player?.firstName ?? '';
        return descending
          ? (bVal as string).localeCompare(aVal as string)
          : (aVal as string).localeCompare(bVal as string);
      } else {
        aVal = a.player[metricKey]?.find((m: any) => m?.isPrimary)?.[metricKey] ?? 0;
        bVal = b.player[metricKey]?.find((m: any) => m?.isPrimary)?.[metricKey] ?? 0;
      }
  
      return descending ? (bVal as number) - (aVal as number) : (aVal as number) - (bVal as number);
    });
  };

  function fetchPinnedPlayers () {
    setLoading(true);

    gql(`
      pinnedPlayers {
        id
        playerId
        player {
          id
          firstName
          lastName
          photoUrl
          pai
          ppi
          combine
          multiSport
          slug
          startsSeason
          gamesSeason
          snapsSeason
          tfPosPerc
          hasHighSchool
          hasCollegeTeam
          hasNFLTeam
          playerColleges {
            id
            isPrimary
            recruitingClass
            positions
            team {
              id
              name
              conference {
                name
                level
                mainLevel
                id
              }
            }
          }
          highSchools
          hsCombines {
            id
          }
        }
        order
        position
      }
    `)
      .then((data:any) => data.pinnedPlayers as PinnedPlayer[])
      .then((players:PinnedPlayer[]) => {
        if (players.length) {
          if (selectedBoardDetails) {
            let order = selectedBoardDetails?.order || [];
            updateAndSetPlayersData(orderPlayers(players, order.map(Number)), true);
          } else {
            updateAndSetPlayersData(orderPlayersByOrder(players), true);
          }
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  function orderPlayersByOrder(players:PinnedPlayer[]) {
  
    // 1. First reorder based on playerIds
    const ordered = players.filter(player => player.order !== null).sort((a, b) => a.order - b.order);
  
    // 2. Then append the rest (not included in playerIds)
    const remaining = players.filter(player => player.order == null);
  
    return [...ordered, ...remaining];
  }

  function orderPlayers(players:PinnedPlayer[], playerIds:number[]) {
    const idSet = new Set(playerIds);
  
    // 1. First reorder based on playerIds
    const ordered = playerIds.map(id => players.find(player => player?.id === id)).filter((player): player is PinnedPlayer => player !== undefined);
  
    // 2. Then append the rest (not included in playerIds)
    const remaining = players.filter(player => !idSet.has(player?.id));
  
    return [...ordered, ...remaining];
  }

  function updateAndSetPlayersData (pinnedPlayers:PinnedPlayer[], setPlayersState: boolean = false) {
    setPinnedPlayers(pinnedPlayers);
    if (setPlayersState) {
      setPlayers(pinnedPlayers);
      updateClasses(pinnedPlayers);
      updatePositions(pinnedPlayers);
      groupPlayersByPosition(pinnedPlayers);
    }
  }

  function reorderPinnedPlayers (playerIds:number[], reorderedPlayers:PinnedPlayer[]) {
    if (selectedBoardDetails) {
      gql(`
        mutation {
          updateBoard (
          Board: {
            id: ${Number(selectedBoardDetails?.id)},
            order: [${playerIds}],
          }
          )
        }
      `)
      .then((data: any) => data && data.updateBoard as boolean)
      .then((success: boolean) => {
        if (success) {
          // getBoards();
          getboardById(selectedBoardDetails?.id.toString());
          fetchPinnedPlayers();
          // showToast('Success.', ToastType.SUCCESS);
          setPinnedPlayers(reorderedPlayers);
        }
      })
    } else {

      setLoading(true);
  
      gql(`
        mutation {
          reorderPinnedPlayers (
            pinnedPlayersIds: [${playerIds}],
            position: "${selectedPositions}"
          )
        }
      `)
        .then((data: any) => data && data.reorderPinnedPlayers as boolean)
        .then((success: boolean) => {
          if (success) {
            fetchPinnedPlayers();
            showToast('Success.', ToastType.SUCCESS);
            setPinnedPlayers(reorderedPlayers);
          } else {
            showToast('Failed to save the new order. Please refresh the page.', ToastType.ERROR);
          }
        })
        .catch(() => {
          showToast('Failed to save the new order. Please refresh the page.', ToastType.ERROR);
        })
        .finally(() => setLoading(false));
    }
  }

  function groupPlayersByPosition(players:PinnedPlayer[]) {
    const groupedPlayers = [...players]
      .sort((a:PinnedPlayer, b:PinnedPlayer) => {
        if (a.position < b.position) { return -1; }
        if (a.position > b.position) { return 1; }
        return a.order - b.order;
      });

    setPinnedPlayers(groupedPlayers);

    let idsMapping:{[key: number]: number} = {};
    groupedPlayers.map((pinnedPlayer: any) => {
      idsMapping[pinnedPlayer.playerId] = pinnedPlayer.id;
    });
    setPlayersIdsMapping(idsMapping);

    setPlayers(groupedPlayers);
  }

  function updatePositions(players:PinnedPlayer[]) {
    const positions = players
      .map(player => player.position)
      .sort();

      setPositions(positions
        .filter((position:Position, index:number) => positions.indexOf(position) === index)
        .map((position:Position) => {
          return {
            content: position,
            value: position
          }
        })
      );
  }

  function updateClasses(players:PinnedPlayer[]) {
    const accessLevel = user.accessLevel;
    let graduatingClasses: any[] = [];
    if (rolloverDayNfl !== undefined && rolloverMonthNfl !== undefined && currentRecruitingYear !== undefined) {
      const gradYears = getGradClassYearsList(
        rolloverDayNfl,
        rolloverMonthNfl,
        accessLevel?.hsYearsAugust1 || [],
        accessLevel?.hsYearsMarch1 || [],
        currentRecruitingYear,
        true
      );
      
      graduatingClasses = Array.from(
        new Set(gradYears)
        ).sort((a, b) => b - a)
        .map(year => ({ content: year, value: year }));
        
    }

    setGraduatingClasses(graduatingClasses);
  }

  function onDragEnd (result:any) {
    if (!result.destination) return;

    const fromPosition = result.source.index;
    const toPosition = result.destination.index;
    const reorderedPlayers = [...pinnedPlayers];
    const draggedPlayer = pinnedPlayers[fromPosition];

    reorderedPlayers.splice(fromPosition, 1); // cut out the dragged player
    reorderedPlayers.splice(toPosition, 0, draggedPlayer); // insert it to the position

    const playerIds = reorderedPlayers.map((pinnedPlayer:PinnedPlayer) => playersIdsMapping[pinnedPlayer.player.id]);

    reorderPinnedPlayers(playerIds, reorderedPlayers);
  }

  function onXLSDownload () {
    setLoading(true);
    const playerIds = selectedPlayers?.length 
      ? selectedPlayers?.map((player:Player) => player.id)
      : players.map((pinnedPlayer:PinnedPlayer) => pinnedPlayer.playerId);

    exportXLSReport(
      XLSExportType.PLAYERS,
      { ids: playerIds, board: true },
    )
      .catch(error => {
        if (error.message === ERROR_MESSAGE_CHECK_EMAIL) {
          showToast('Check e-mail. You should receive the download link in several minutes', ToastType.SUCCESS);
        } else {
          console.error(error);
          showToast(<>Failed to download XLS report. <br />({error.message})</>, ToastType.ERROR);
        }
      })
      .finally(() => setLoading(false));
  }

  function onPDFDownload () {
    setLoading(true);

    exportPDFReport(
      PDFExportPage.RECRUITING_BOARD,
      '',
      selectedPositions?.length ? { position: selectedPositions } : undefined,
    )
      .catch(error => {
        console.error(error);
        showToast(<>Failed to download PDF report. <br />({error.message})</>, ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function showToast (message:any, type:ToastType = ToastType.SUCCESS) {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  }

  function onCheckBoxChange (player:Player) {
    return (value: boolean) => {
      if (value) {
        setSelectedPlayers([...selectedPlayers, player]);
      } else {
        const selectedItemsWithoutCheckedItem = [...selectedPlayers];
        const removeIndex = selectedPlayers.findIndex((item) => item.id === player.id);
        if (removeIndex > -1) {
          selectedItemsWithoutCheckedItem.splice(removeIndex, 1);
        }
        setSelectedPlayers(selectedItemsWithoutCheckedItem);
      }
    };
  }

  function onAlert () {
    setLoading(true);

    gql(`
      mutation {
        addAlerts (playerIds: [${selectedPlayers.map((selectedItem:Player) => selectedItem.id)}])
      }
    `)
      .then((data:any) => ({
        success: data.addAlerts as boolean,
        errorMessage: data.errorMessage,
      }))
      .then(({ success, errorMessage }:{ success:boolean, errorMessage:string }) => {
        if (success) {
          showToast('Alert added', ToastType.SUCCESS);
        } else if (errorMessage) {
          showToast(errorMessage, ToastType.ERROR);
        } else {
          showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));

    setSelectedPlayers([]);
  }

  function onSave () {
    setLoading(true);

    gql(`
      mutation {
        savePlayers (playerIds: [${selectedPlayers.map((selectedItem:Player) => selectedItem.id)}], folderId: null)
      }
    `)
      .then((data:any) => ({
        success: data.savePlayers as boolean,
        errorMessage: data.errorMessage,
      }))
      .then(({ success, errorMessage }:{ success:boolean, errorMessage:string }) => {
        if (success) {
          showToast('Saved', ToastType.SUCCESS);
        } else if (errorMessage) {
          showToast(errorMessage, ToastType.ERROR);
        } else {
          showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));

    setSelectedPlayers([]);
  }

  function renderTableRow (pinnedPlayer:PinnedPlayer, index:number) {
    const {
      player: {
        id,
        firstName,
        lastName,
        photoUrl,
        pai,
        combine,
        playerColleges,
        highSchools,
        hsCombines,
        ppi,
        startsSeason,
        gamesSeason,
        snapsSeason,
        tfPosPerc
      },
      position
    } = pinnedPlayer;

    const primaryHighSchool = (highSchools || []).find((highSchool:HighSchool) => !!highSchool?.isPrimary);
    const primaryCollege = (playerColleges || []).find((college:College) => !!college?.isPrimary);

    const itemId = `${id}_${index}`;
    const checked = !!selectedPlayers.find(selectedPlayer => selectedPlayer.id === id);
    const startsVal = startsSeason ?? 0;
    const gamesVal = gamesSeason ?? 0;
    const snapsVal = snapsSeason ?? 0;
    const posRank = !!tfPosPerc ? `${Math.round(Number(tfPosPerc))}%` : '';

    return (
      <Draggable
        key={itemId}
        index={index}
        draggableId={itemId}
        isDragDisabled={isDragDisabled}
      >
        {({ draggableProps, dragHandleProps, innerRef }, snapshot) => (
          <TableRow
            className={clsx(classes.tableRow, snapshot.isDragging && classes.tableRowDragging)}
            ref={innerRef}
            {...draggableProps}
            {...dragHandleProps}
          >
            {!isDragDisabled && 
            <TableCell className={clsx(classes.tableCell, printed && classes.blankCell)}>
              <DragNDropDots className={classes.dragNDropDots} />
            </TableCell>
              }

            <TableCell className={clsx(classes.tableCell, classes.checkBoxCell)}>
              <Checkbox
                checked={checked}
                onChange={onCheckBoxChange(pinnedPlayer?.player)}
              />
            </TableCell>

            <TableCell className={classes.tableCell} align='left'>
              <div className={classes.playerInfoContainer}>
                <Avatar
                  className={classes.playerAvatar}
                  src={photoUrl}
                  alt='Profile picture'
                />

                <div className={classes.playerNameAndCollege}>
                  <PlayerNameWithPopover
                    playerId={id}
                    name={`${firstName} ${lastName}`}
                  />

                  {(primaryCollege?.team?.name && position) && (
                    <div className={classes.playerCollege}>{position} - {primaryCollege.team.name}</div>
                  )}
                </div>

                {primaryHighSchool?.state && (
                  <LocationIcon
                    className={classes.locationIcon}
                    stateCode={primaryHighSchool.state}
                  />
                )}
              </div>
            </TableCell>

            <TableCell className={classes.tableCell}>
              {(primaryHighSchool && primaryHighSchool.graduatingClass)
                ? primaryHighSchool.graduatingClass
                : <NotAvailable />
              }
            </TableCell>

            <TableCell className={classes.tableCell}>
              <Score
                type={SCORE_TYPE.PAI}
                scoreList={pai}
              />
            </TableCell>

            <TableCell className={clsx(classes.tableCell, classes.hsComTableCell)}>
              <Score
                type={SCORE_TYPE.COMBINE}
                scoreList={combine}
                combines={hsCombines}
              />
            </TableCell>

            <TableCell className={classes.tableCell} align='center'>
              <Score
                type={SCORE_TYPE.PPI}
                value={ppi || undefined}
              />
            </TableCell>

            <TableCell className={clsx(classes.tableCell, classes.tfPosPercCell)} align='center'>
              {posRank}
            </TableCell>

            <TableCell className={clsx(classes.tableCell)} align='left'>
              <div className={classes.seasonalBlock}>
                <Typography><b>{startsVal}</b> Starts</Typography>
                <Typography><b>{gamesVal}</b> Games</Typography>
                <Typography><b>{snapsVal}</b> Snaps</Typography>
              </div>
            </TableCell>
          </TableRow>
        )}
      </Draggable>
    );
  }

  function unpinPlayers(playerIds:(number | string | undefined) []) {
    return gql(`
      mutation {
        unpin (playerIds: [${playerIds.map(playerId => playerId)}])
      }
    `)
      .then((data:any) => data && data.unpin as boolean)
      .catch(() => {
        showToast('Oops, something is wrong. Try again or contact our Support team.', ToastType.ERROR);
      });
  }

  function unpinSinglePlayer() {
    setLoading(true);
    const playerIds = selectedPlayers.map((selectedItem:Player) => selectedItem.id);
    unpinPlayers(playerIds)
      .then((success:boolean | void) => {
        if (success) {
          showToast('Unpinned', ToastType.SUCCESS);
          const pinndePlayersAfterUnpin = pinnedPlayers.filter((player:PinnedPlayer) => player?.playerId && !playerIds.includes(player?.playerId));
          updateAndSetPlayersData(pinndePlayersAfterUnpin);
        } else {
          showToast('Oops, something is wrong. Try again or contact our Support team.', ToastType.ERROR);
        }
      })
      .finally(() => setLoading(false));
  }

  function handleOpenClearDialog() {
    setDialogOpen(true);
  }

  function clearBoard() {
    setLoading(true);
    setDialogOpen(false);

    unpinPlayers(pinnedPlayers.map(pinnedPlayer => pinnedPlayer.playerId))
      .then((success:boolean | void) => {
        if (success) {
          showToast('All players unpinned', ToastType.SUCCESS);
          updateAndSetPlayersData([]);
        } else {
          showToast('Oops, something is wrong. Try again or contact our Support team.', ToastType.ERROR);
        }
      })
      .finally(() => {
        setLoading(false);
        setSelectedPositions([position ?? '']);
      });
  }

  function handleSortClick(metric: 'pai' | 'combine' | 'class' | 'ppi' | 'posRank' | 'name') {
    setSortConfig(prev => {
      // switching to a new metric, reset order
      if (prev.metric !== metric) {
        return { metric, order: 'asc' };
      }
  
      if (prev.order === 'asc') return { metric, order: 'desc' };
      return { metric, order: 'asc' };
    });
  }  

  function handlePositionChange(newValues: string[]) {
    const allValue = 'all';
    const allOptionValues = positions.map((p: { value: any; }) => p.value);
  
    const isAllChecked = newValues.includes(allValue);
    const isAllOptionSelected = selectedPositions.includes(allValue);
    const isCurrentlyAllSelected =
      selectedPositions.length === allOptionValues.length + 1 && isAllOptionSelected;
    let filteredPositions: string[] = []

    // || (!isAllChecked && newValues.length < selectedPositions?.length)
    if ((isAllChecked && !isCurrentlyAllSelected)) {
      // "All" was clicked and not everything is selected → select all
      filteredPositions = [allValue, ...allOptionValues];
    } else if (!isAllChecked && isAllOptionSelected) {
      // "All" was unchecked → reset selection
      filteredPositions = [];
    } else if (isAllChecked && newValues.length < selectedPositions?.length) {
      const filtered = newValues.filter(val => val !== allValue);
      filteredPositions = filtered;
    } else if (!isAllChecked && !isAllOptionSelected && newValues?.length === positions?.length) {
      // "All" was checked and all positions selected, uncheck "all" 
      filteredPositions = [allValue, ...allOptionValues];
    } else {
      // Normal multi-select behavior
      filteredPositions = newValues;
    }
    setSelectedPositions(filteredPositions);
    updateBoard(filteredPositions, FILTER_TYPE.POSITION)
  };

  function handleClassChange(newValues: string[]) {
    const allValue = 'all';
    const allOptionValues = graduatingClasses.map((p: { value: any; }) => p.value);
  
    const isAllChecked = newValues.includes(allValue);
    const isAllOptionSelected = selectedGradClasses.includes(allValue);
    const isCurrentlyAllSelected =
    selectedGradClasses.length === allOptionValues.length + 1 && isAllOptionSelected;
    let filteredClasses: string[] = [];

    if ((isAllChecked && !isCurrentlyAllSelected)) {
      // "All" was clicked and not everything is selected → select all
      filteredClasses = [allValue, ...allOptionValues];
    } else if (!isAllChecked && isAllOptionSelected) {
      // "All" was unchecked → reset selection
      filteredClasses = [];
    } else if (isAllChecked && newValues.length < selectedGradClasses?.length) {
      const filtered = newValues.filter(val => val !== allValue);
      filteredClasses = filtered;
    } else if (!isAllChecked && !isAllOptionSelected && newValues?.length === graduatingClasses?.length) {
      // "All" was checked and all classes selected, uncheck "all" 
      filteredClasses = [allValue, ...allOptionValues];
    } else {
      // Normal multi-select behavior
      filteredClasses = newValues;
    }
    setSelectedGradClasses(filteredClasses);
    // update board on filter change when there is a selected board
    updateBoard(filteredClasses, FILTER_TYPE.CLASS)
  };

  function handleCollegeTypeChange(newValues: string[]) {
    const allValue = 'all';
    const allOptionValues = selectedConference.map((p: { value: any; }) => p.value);
  
    const isAllChecked = newValues.includes(allValue);
    const isAllOptionSelected = selectedCollegeType.includes(allValue);
    const isCurrentlyAllSelected =
    selectedCollegeType.length === allOptionValues.length + 1 && isAllOptionSelected;
    let filteredCollegeType: string[] = []

    if ((isAllChecked && !isCurrentlyAllSelected)) {
      // "All" was clicked and not everything is selected → select all
      filteredCollegeType = [allValue, ...allOptionValues];
    } else if (!isAllChecked && isAllOptionSelected) {
      // "All" was unchecked → reset selection
      filteredCollegeType = [];
    } else if (isAllChecked && newValues.length < selectedCollegeType?.length) {
      const filtered = newValues.filter(val => val !== allValue);
      filteredCollegeType = filtered;
    } else if (!isAllChecked && !isAllOptionSelected && newValues?.length === selectedConference?.length) {
      // "All" was checked and all classes selected, uncheck "all" 
      filteredCollegeType = [allValue, ...allOptionValues];
    } else {
      // Normal multi-select behavior
      filteredCollegeType = newValues;
    }
    setSelectedCollegeType(filteredCollegeType);
    // update board on filter change when there is a selected board
    updateBoard(filteredCollegeType, FILTER_TYPE.COLLEGE_TYPE)
  }

  function getBoards(newBoardName: string = '') {
    setLoading(true);
    gql(`
      getBoards(${isCollege ? `programId: ${programId}` : `userId: ${userId}`}) {
        id
        name
        userId
        programId
        position
        class
        type
        state
        collegeType
        order
        createdAt
        updatedAt
      }
    `)
    .then((data:any) => data.getBoards as any)
    .then((boards: Board[]) => {
      if (boards) {
        setBoardsList(boards);
        if (newBoardName) {
          const newBoard = boards?.find((b) => b.name === newBoardName)
          const boardId = String(newBoard?.id)
          boardId && getboardById(boardId);
        }
      };
    })
    .catch(console.error)
    .finally(() => setLoading(false));
  }

  function addBoardHandler() {
    setLoading(true);
    const positions = filterAllFromSelectedItem(selectedPositions);
    const gradClasses = filterAllFromSelectedItem(selectedGradClasses);
    const collegeType = filterAllFromSelectedItem(selectedCollegeType);

    const boardPayload = {
      name: newBoardName,
      position: positions,
      class: gradClasses,
      collegeType: collegeType,
      programId: programId,
      order: [...pinnedPlayers.map(player => player.id)],
    }
    gql(`
      mutation {
        saveBoard(
          Board: ${serializeGQLObject(boardPayload)}
        )
      }
    `)
      .then((data:any) => ({
        success: data.saveBoard as boolean,
        errorMessage: data.errorMessage,
      }))
      .then(({ success, errorMessage }:{ success:boolean, errorMessage:string }) => {
        if (success) {
          getBoards(newBoardName);
          showToast('Saved', ToastType.SUCCESS);
        } else if (errorMessage) {
          showToast(errorMessage, ToastType.ERROR);
        } else {
          showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => {
        setLoading(false)
        setBoardDialogOpen(false);
        setNewBoardName('');
      });

    setSelectedPlayers([]);
  }

  function deleteBoard() {
    setLoading(true);

    gql(`
      mutation {
        deleteBoard(
          boardId: ${Number(selectedBoard)}
        )
      }
    `)
      .then((data:any) => ({
        success: data.deleteBoard as boolean,
        errorMessage: data.errorMessage,
      }))
      .then(({ success, errorMessage }:{ success:boolean, errorMessage:string }) => {
        if (success) {
          getBoards();
          setSelectedBoard('');
          showToast('Saved', ToastType.SUCCESS);
        } else if (errorMessage) {
          showToast(errorMessage, ToastType.ERROR);
        } else {
          showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
        }
      })
      .catch(error => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => {
        setLoading(false)
        setNewBoardName('');
        setDeleteBoardDialogOpen(false);
        resetFilters() // reset all filters
      });
    setSelectedPlayers([]);
  }

  // update select board
  function updateBoard(selectedItems: any, filterName = '') {
    if (selectedBoard) {
      setLoading(true);
      
      // Use selectedItems dynamically and filter 'all' option from the setters
      const filteredItems = filterAllFromSelectedItem(selectedItems);

      // filter 'all' option from the selected item
      let positions = filterAllFromSelectedItem(selectedPositions);
      let gradClasses = filterAllFromSelectedItem(selectedGradClasses);
      let collegeType = filterAllFromSelectedItem(selectedCollegeType);

      // update latest updated filter
      switch (filterName) {
        case FILTER_TYPE.POSITION:
          positions = filteredItems;
          break;
        case FILTER_TYPE.CLASS:
          gradClasses = filteredItems;
          break;
        case FILTER_TYPE.COLLEGE_TYPE:
          collegeType = filteredItems;
          break;
        default:
          break;
      }

      const boardPayload = {
        id: Number(selectedBoard),
        position: positions,
        class: gradClasses,
        type: collegeType,
        programId: programId,
      };

      gql(`
        mutation {
          updateBoard(
            Board: ${serializeGQLObject(boardPayload)}
          )
        }
      `)
        .then((data:any) => ({
          success: data.updateBoard as boolean,
          errorMessage: data.errorMessage,
        }))
        .then(({ success, errorMessage }:{ success:boolean, errorMessage:string }) => {
          if (success) {
            getBoards();
            showToast('Saved', ToastType.SUCCESS);
          } else if (errorMessage) {
            showToast(errorMessage, ToastType.ERROR);
          } else {
            showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
          }
        })
        .catch(error => {
          console.error(error);
          showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
        })
        .finally(() => setLoading(false)
        );
      setSelectedPlayers([]);
    }
  };

  // get board by id
  function getboardById(id: string) {
    setLoading(true);
    gql(`
      getBoardsById(boardId: ${Number(id)}) 
      {
        id
        name
        userId
        programId
        position
        class
        type
        state
        collegeType
        order
        createdAt
        updatedAt
      }
    `)
    .then((data:any) => data.getBoardsById as any)
    .then((board: Board) => {
      setSelectedBoardDetails(board);
      setSelectedBoard(board?.id.toString() || '');
      setSelectedPositions(positions?.length === board?.position?.length ? ['all', ...(board?.position || [])] : (board?.position || []));
      setSelectedGradClasses(graduatingClasses?.length === board?.class?.length ? ['all', ...(board?.class || [])] : (board?.class || []));
      setSelectedCollegeType(selectedConference?.length === board?.collegeType?.length ? ['all', ...(board?.collegeType || [])] : (board?.collegeType || []));
    })
    .catch(console.error)
    .finally(() => setLoading(false));
  }

  // filter 'all' option from the selected filter list
  function filterAllFromSelectedItem(filterItem: any) {
    return filterItem?.filter((data: string) => data !== 'all') ?? [];
  }

  // reset filters 
  function resetFilters() {
    setSelectedPositions([]);
    setSelectedGradClasses([]);
    setSelectedCollegeType([]); 
  };

  const actionDisabled = !selectedPlayers.length;
  const compareActionDisabled = !selectedPlayers.length || selectedPlayers.length > 1;
  const sideBySideActionDisabled = (
    !selectedPlayers.length ||
    selectedPlayers.length === 1 ||
    selectedPlayers.length > 10
  );
  const boardClearable = selectedPlayers?.length === pinnedPlayers?.length;
  const isBoardSelected = selectedBoard !== '';

  return (
    <Paper className={clsx(classes.myEvaluationBoard, className)}>
      <Loader inProgress={loading} />

      {!printed && (
        <div className={classes.header}>
          <div className={classes.headerTitleWrap}>
            <PinIcon className={classes.headerIcon} />
            <h2 className={classes.headerTitle}>
              My Evaluation Board
              ({pinnedPlayers?.length || 0})
            </h2>
          </div>

          <div className={classes.boardFilterWrap}>
            <DropDown
              className={classes.dropDown}
              selectorClassName={classes.dropDownSelect}
              selectorRootClassName={classes.selectorRootClassName}
              labelWhenSelectedNone='Select Board'
              labelWhenSelectedAll='Select Board'
              showLabelWhenSelectedNoneWithEmptyItem={true}
              emptyItem={{
                value: '',
                content: 'None (Primary)'
              }}
              items={boardDropdownItems}
              value={selectedBoard}
              onChange={(board:string) => {
                getboardById(board)
              }}
            />
            
            {/* Add Board */}
            <Button
              className={classes.addBoardButton}
              primary={true}
              type='button'
              disabled={!!selectedBoard || disableNewBoard}
              onClick={() => {
                setBoardDialogOpen(true);
              }}
            >
              <PlusIcon className={classes.addIcon} />
              <span>New Board</span>
            </Button>

            {/* Delete Board */}
            <Button
              className={classes.addBoardButton}
              primary={true}
              type='button'
              disabled={!selectedBoard}
              onClick={() => {
                setDeleteBoardDialogOpen(true)
              }}
            >
              {/* <PlusIcon className={classes.addIcon} /> */}
              <span>Delete Board</span>
            </Button>
          </div>
        </div>
      )}

      <Divider variant='fullWidth' light />
      <div className={classes.filterWrap}>  
        <MultiSelect
          className={classes.multiSelect}
          selectorClassName={classes.multiSelectSelector}
          selectorRootClassName={classes.multiSelectSelectorRoot}
          labelWhenSelectedNone='By Position'
          labelWhenSelectedAll='By Position'
          showLabelWhenSelectedNoneWithEmptyItem={true}
          items={positions}
          values={selectedPositions}
          emptyItem={{
            content: 'All', value: 'all'
          }}
          onChange={(newValues: string[]) =>
            handlePositionChange(newValues)
          }
          optionsDisbled={isBoardSelected}
        />

        <MultiSelect
          className={classes.multiSelect}
          selectorClassName={classes.multiSelectSelector}
          selectorRootClassName={classes.multiSelectSelectorRoot}
          labelWhenSelectedNone='By Class'
          labelWhenSelectedAll='By Class'
          showLabelWhenSelectedNoneWithEmptyItem={true}
          items={graduatingClasses || []}
          values={selectedGradClasses}
          emptyItem={{
            content: 'All', value: 'all'
          }}
          onChange={(newValues: string[]) =>
            handleClassChange(newValues)
          }
          optionsDisbled={isBoardSelected}
        />
        <MultiSelect
          className={classes.multiSelect}
          selectorClassName={classes.multiSelectSelector}
          selectorRootClassName={classes.multiSelectSelectorRoot}
          labelWhenSelectedNone='By Type'
          labelWhenSelectedAll='By Type'
          showLabelWhenSelectedNoneWithEmptyItem={true}
          items={selectedConference}
          values={selectedCollegeType}
          emptyItem={{
            content: 'All', value: 'all'
          }}
          onChange={(newValues: string[]) =>
            handleCollegeTypeChange(newValues)
          }
          optionsDisbled={isBoardSelected}
        />

        <Action
          className={clsx(classes.action, classes.colorPrimary)}
          onClick={() => {
            setRefreshBoards(true);
          }}
          icon={RefreshIcon}
          iconClassName={classes.actionIcon}
        >
          Refresh
        </Action>
      </div>

      <div className={classes.actions}>
        <div className={classes.actionsPrimary}>
          <Action
            className={clsx(classes.action, sideBySideActionDisabled && classes.disabledAction)}
            icon={SideBySideIcon}
            iconClassName={classes.actionIcon}
            disabled={sideBySideActionDisabled}
            href={`/side-by-side?playerIds=${selectedPlayers.map((player:Player) => player.id).join(',')}`}
          >
            <span className={classes.actionText}>Side-by-Side</span>
          </Action>

          <Action
            className={clsx(classes.action, compareActionDisabled && classes.disabledAction)}
            icon={CompareIcon}
            iconClassName={classes.actionIcon}
            disabled={compareActionDisabled}
            href={!compareActionDisabled ? `/compare/${selectedPlayers[0].slug}` : '/compare'}
          >
            <span className={classes.actionText}>Compare</span>
          </Action>

          <Action
            className={clsx(classes.action, actionDisabled && classes.disabledAction)}
            icon={AlertIcon}
            iconClassName={classes.actionIcon}
            disabled={actionDisabled}
            onClick={onAlert}
          >
            <span className={classes.actionText}>Alert</span>
          </Action>

          <Action
            className={clsx(classes.action, actionDisabled && classes.disabledAction)}
            icon={SaveIcon}
            iconClassName={classes.actionIcon}
            disabled={actionDisabled}
            onClick={onSave}
          >
            <span className={classes.actionText}>Save</span>
          </Action>

          <Action
            className={clsx(classes.action, actionDisabled && classes.disabledAction)}
            icon={PinIcon}
            iconClassName={classes.actionIcon}
            disabled={actionDisabled}
            onClick={handleOpenClearDialog}
          >
            <span className={classes.actionText}>Unpin</span>
          </Action>
        </div>
        <div className={classes.headerDownloads}>
          {(user.accessLevel.exportAllowanceType !== 'none') && (
            <Download
              label='XLS'
              onClick={onXLSDownload}
              disabled={!players.length}
            />
          )}

          <Download
            label='PDF'
            onClick={onPDFDownload}
            disabled={!players.length}
          />
        </div>
      </div>

      <Divider variant='fullWidth' light />

      {!players.length && (
        <div className={classes.noPlayers}>
          <UpdateIcon className={classes.noPlayersIcon} />
          <div className={classes.noPlayersText}>No Player Pinned</div>
        </div>
      )}

      {players.length > 0 && (
        <div className={classes.tableWrap}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId='evaluation-board'>
              {({ droppableProps, placeholder, innerRef }) => (
                <div
                  ref={innerRef}
                  {...droppableProps}
                >
                  <Table stickyHeader aria-label="sticky_recruiting_table">
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        {!isDragDisabled ? <TableCell className={clsx(classes.tableHeadCell, printed && classes.blankCell)}>&nbsp;</TableCell> : ''}
                        <TableCell
                          key='check-box-cell'
                          className={classes.checkBoxCell}
                        >
                          <Checkbox
                            checked={selectedPlayers.length === pinnedPlayers.length}
                            onChange={() => selectedPlayers.length === pinnedPlayers.length
                              ? setSelectedPlayers([])
                              : setSelectedPlayers(pinnedPlayers.map((pinnedPlayer) => pinnedPlayer.player))
                            }
                          />
                        </TableCell>
                        <SortableTableHeaderCell
                          label={'Name | College'}
                          order={sortConfig.metric === 'name' ? sortConfig.order : undefined}
                          onClick={() => handleSortClick('name')}
                          tableHeadCellClassName={classes.tableHeadCell}
                          orderIconClassName={classes.orderIcon}
                          orderIconActiveClassName={classes.orderIconActive}
                        />

                        <SortableTableHeaderCell
                          label="Class"
                          order={sortConfig.metric === 'class' ? sortConfig.order : undefined}
                          onClick={() => handleSortClick('class')}
                          tableHeadCellClassName={classes.tableHeadCell}
                          orderIconClassName={classes.orderIcon}
                          orderIconActiveClassName={classes.orderIconActive}
                        />
                        <SortableTableHeaderCell
                          label="PAI"
                          order={sortConfig.metric === 'pai' ? sortConfig.order : undefined}
                          onClick={() => handleSortClick('pai')}
                          tableHeadCellClassName={classes.tableHeadCell}
                          orderIconClassName={classes.orderIcon}
                          orderIconActiveClassName={classes.orderIconActive}
                        />

                        <SortableTableHeaderCell
                          label="HS Com."
                          order={sortConfig.metric === 'combine' ? sortConfig.order : undefined}
                          onClick={() => handleSortClick('combine')}
                          tableHeadCellClassName={classes.tableHeadCell}
                          orderIconClassName={classes.orderIcon}
                          orderIconActiveClassName={classes.orderIconActive}
                        />
                        <SortableTableHeaderCell
                          label="PPI"
                          order={sortConfig.metric === 'ppi' ? sortConfig.order : undefined}
                          onClick={() => handleSortClick('ppi')}
                          tableHeadCellClassName={classes.tableHeadCell}
                          orderIconClassName={classes.orderIcon}
                          orderIconActiveClassName={classes.orderIconActive}
                        />
                        <SortableTableHeaderCell
                          label="Position Rank"
                          order={sortConfig.metric === 'posRank' ? sortConfig.order : undefined}
                          onClick={() => handleSortClick('posRank')}
                          tableHeadCellClassName={classes.tableHeadCell}
                          orderIconClassName={classes.orderIcon}
                          orderIconActiveClassName={classes.orderIconActive}
                        />
                        <TableCell className={clsx(classes.tableHeadCell, classes.careerLogHeadCell)} align='left'>Career Log</TableCell>
                        {!isDragDisabled ? <TableCell className={clsx(classes.tableHeadCell, printed && classes.blankCell)}>&nbsp;</TableCell> : ''}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pinnedPlayers.map(renderTableRow)}
                      {placeholder}
                    </TableBody>
                  </Table>
                  {!pinnedPlayers?.length && (
                    <div className={classes.noDataTextWrapper}>
                      <Typography className={classes.noDataText}>No Data Found</Typography>
                    </div>
                  )}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}

      <Toast
        visible={toastVisible}
        type={toastType}
        onHide={() => setToastVisible(false)}
      >
        {toastMessage}
      </Toast>

      <ConfirmDialog
        title={`Remove ${boardClearable ? 'all players' : 'player'} from your primary and saved boards?`}
        confirmActionText={boardClearable ? 'Yes, Clear the board' : 'Yes, Confirm'}
        open={dialogOpen}
        onConfirm={boardClearable ? clearBoard :  unpinSinglePlayer}
        onClose={() => setDialogOpen(false)}
        onCancel={() => setDialogOpen(false)}
      />

      {/* Add board dialog */}
      <Dialog
        open={boardDialogOpen}
        title='Save New Board'
        onClose={() => setBoardDialogOpen(false)}
        actions={[
          {
            name: 'Cancel',
            onClick: () => {
              setBoardDialogOpen(false);
              setNewBoardName('');
            },
          },
          {
            name: 'Confirm',
            primary: true,
            disabled: !newBoardName.trim(),
            loading,
            onClick: () => {
              addBoardHandler();
            },
          }
        ]}
      >
        <div className={classes.boardDialogContent}>
          <Input
            className={classes.boardDialogInput}
            label='Board Name'
            value={newBoardName}
            onChange={(value:string) => setNewBoardName(value)}
          />
        </div>
      </Dialog>

      {/* Delete board dialog */}
      <ConfirmDialog
        title='Are you sure you want to delete?'
        open={deleteBoardDialogOpen}
        onConfirm={deleteBoard}
        onClose={() => setDeleteBoardDialogOpen(false)}
        onCancel={() => setDeleteBoardDialogOpen(false)}
      />
    </Paper>
  );
}

// const mapDispatchToProps = (dispatch: Dispatch) => {
//   return bindActionCreators({
//     // fetchFBSConferences,
//   }, dispatch);
// };

// const mapStateToProps = (state: State) => {
//   return {
//     fbsConferences: state.conferences.fbsConferences,
//   };
// };

// export default connect(mapStateToProps, mapDispatchToProps)(MyEvaluationBoard);